import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import JuicesManager from "@/components/admin/JuicesManager";
import RecipesManager from "@/components/admin/RecipesManager";
import SubscribersManager from "@/components/admin/SubscribersManager";

const TABS = [
  { key: "juices", label: "Juices" },
  { key: "recipes", label: "Recipes" },
  { key: "subscribers", label: "Subscribers" },
];

const LoginScreen = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
    } else {
      toast.success("Welcome back, Holly!");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFF8F0] px-5" data-testid="admin-login">
      <div className="w-full max-w-sm">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1A1A1A]/50 hover:text-[#FF7A1A] mb-6" data-testid="back-home-link">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
        <div className="bg-white rounded-3xl p-8 shadow-[0_20px_50px_rgba(255,122,26,0.15)] border border-[#1A1A1A]/5">
          <img src="/holly-logo.png" alt="Holly's Juices" className="h-14 w-auto object-contain mx-auto mb-2" />
          <h1 className="font-display font-extrabold text-2xl text-center text-[#1A1A1A]">Admin Login</h1>
          <p className="text-center text-sm text-[#1A1A1A]/50 mt-1 mb-6">Manage juices, recipes & subscribers</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-[#1A1A1A]/70">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full mt-1 rounded-xl border border-[#1A1A1A]/15 px-4 py-3 outline-none focus:ring-2 focus:ring-[#FF7A1A]/40" data-testid="admin-email-input" />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#1A1A1A]/70">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full mt-1 rounded-xl border border-[#1A1A1A]/15 px-4 py-3 outline-none focus:ring-2 focus:ring-[#FF7A1A]/40" data-testid="admin-password-input" />
            </div>
            {error && <p className="text-sm text-[#FF3D68] font-medium" data-testid="admin-login-error">{error}</p>}
            <button type="submit" disabled={loading} className="w-full rounded-full bg-[#FF7A1A] hover:bg-[#FFC93C] hover:text-[#1A1A1A] text-white font-bold py-3 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="admin-login-submit">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("juices");

  return (
    <div className="min-h-screen bg-[#FFF8F0]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#FFF8F0]/90 border-b border-[#FF7A1A]/10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/holly-logo.png" alt="Holly's Juices" className="h-10 w-auto object-contain" />
            <span className="hidden sm:inline font-display font-bold text-[#1A1A1A]/40 text-sm">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm font-semibold text-[#1A1A1A]/60 hover:text-[#FF7A1A]" data-testid="view-site-link">View site</Link>
            <button onClick={logout} className="inline-flex items-center gap-1.5 rounded-full bg-[#1A1A1A] text-white font-bold text-sm px-4 py-2 hover:bg-[#FF3D68] transition-colors" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
        <div className="inline-flex bg-white rounded-full p-1.5 border border-[#1A1A1A]/5 shadow-sm mb-8">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-5 sm:px-7 py-2.5 rounded-full font-bold text-sm transition-all ${tab === t.key ? "bg-[#FF7A1A] text-white shadow" : "text-[#1A1A1A]/60 hover:text-[#1A1A1A]"}`}
              data-testid={`tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "juices" && <JuicesManager />}
        {tab === "recipes" && <RecipesManager />}
        {tab === "subscribers" && <SubscribersManager />}
      </div>
    </div>
  );
};

const Admin = () => {
  const { user, checking } = useAuth();

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF8F0]">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF7A1A]" />
      </div>
    );
  }

  return user && user !== false ? <Dashboard /> : <LoginScreen />;
};

export default Admin;
